/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { getDataThunkType } from '@europrocurement/l2d-redux-utils';
import { useModal, EmptyBox } from '@europrocurement/flexy-components';
import { LoadingModal } from './LoadingModal';

import { useReminderPublisherCreation } from '../hooks/useReminderPublisherCreation';
import { useReminderSuggestions } from '../hooks/useReminderSuggestions';
import { ReminderStepperContent } from './ReminderStepperContent';
import { SocietySelectionStep } from './steps/SocietySelectionStep';
import { SuggestionSelectionStep } from './steps/SuggestionSelectionStep';
import { CustomMessageStep } from './steps/CustomMessageStep';
import { ReminderCreationStep, SocietyEnumValue } from '../types/reminderPublisherTypes';

interface ReminderPublisherModalProps {
    fetchDataAfterSuccess?: getDataThunkType<unknown>;
    onProcessingStart?: () => void;
    onProcessingEnd?: () => void;
}

/**
 * Composant pour gérer la création des rappels fournisseurs
 */
export const ReminderPublisherModal = React.forwardRef<
    { openModal: () => void },
    ReminderPublisherModalProps
>(({ fetchDataAfterSuccess, onProcessingStart, onProcessingEnd }, ref) => {
    const { modalActions } = useModal();

    // Référence pour contrôler les rendus
    const renderCountRef = React.useRef(0);

    // État pour suivre si la modal est activée
    const [isModalActive, setIsModalActive] = React.useState(false);

    // État pour suivre l'étape courante
    const [currentStep, setCurrentStep] = React.useState<ReminderCreationStep>('society');

    // État pour stocker les messages d'erreur éventuels
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

    // État pour afficher un état de chargement général
    const [isLoading, setIsLoading] = React.useState(false);

    // État spécifique pour le chargement lors de la création des rappels
    const [isCreatingReminders, setIsCreatingReminders] = React.useState(false);

    // État pour suivre si les suggestions ont été chargées pour cette étape
    const [isSuggestionsLoaded, setIsSuggestionsLoaded] = React.useState(false);

    // État pour forcer explicitement un rendu de la modal
    const [renderTrigger, setRenderTrigger] = React.useState(0);

    // État local pour la société sélectionnée
    const [selectedSocietyId, setSelectedSocietyId] = React.useState<SocietyEnumValue | null>(12);

    // Récupération des suggestions
    const { suggestions, fetchSuggestions } = useReminderSuggestions();

    // Gestion de l'état de création
    const {
        state,
        handleSocietySelect,
        setSuggestions,
        setSelectedSuggestions,
        setCustomMessage,
        createReminderPublishers,
        resetState,
    } = useReminderPublisherCreation({
        onCreationSuccess: () => {
            setIsCreatingReminders(false);
            setIsModalActive(false);
            modalActions.close();
            if (onProcessingEnd) onProcessingEnd();
        },
        onCreationError: () => {
            setIsCreatingReminders(false);
            if (onProcessingEnd) onProcessingEnd();
        },
        fetchDataAfterSuccess,
    });

    // Notification du début/fin de traitement
    React.useEffect(() => {
        if (state.isProcessing) {
            if (onProcessingStart) onProcessingStart();
        } else if (onProcessingEnd) onProcessingEnd();
    }, [onProcessingEnd, onProcessingStart, state.isProcessing]);

    // Ferme la modal et réinitialise l'état
    const handleCloseModal = React.useCallback(() => {
        setIsModalActive(false);
        modalActions.close();
        resetState();
        setSelectedSocietyId(12);
        setCurrentStep('society');
        setErrorMessage(null);
        setIsLoading(false);
        setIsCreatingReminders(false);
        setIsSuggestionsLoaded(false);
        renderCountRef.current = 0;
        if (onProcessingEnd) onProcessingEnd();
    }, [modalActions, onProcessingEnd, resetState]);

    // Fonction pour déclencher explicitement un rendu de la modal
    const forceRenderModal = React.useCallback(() => {
        setRenderTrigger((prev) => prev + 1);
    }, []);

    // Handler pour la sélection d'une société
    const handleSocietyChange = React.useCallback(
        (value: any) => {
            // Mettre à jour l'état local et l'état global
            const societyValue = value as SocietyEnumValue;
            setSelectedSocietyId(societyValue);
            handleSocietySelect(societyValue);
        },
        [handleSocietySelect],
    );

    // Fonction pour charger les suggestions
    const loadSuggestions = React.useCallback(async () => {
        try {
            // Définir explicitement le filtre
            const societyIdToUse = selectedSocietyId;

            // Afficher l'interface de chargement
            setIsLoading(true);
            modalActions.call(
                <ReminderStepperContent currentStep="suggestion">
                    <LoadingModal message="Chargement des fournisseurs disponibles..." />
                </ReminderStepperContent>,
            );

            // Charger les suggestions (type conversion pour éviter l'erreur)
            const loadedSuggestions = await fetchSuggestions(
                societyIdToUse as SocietyEnumValue | undefined,
            );

            // Mettre à jour l'état
            setSuggestions(loadedSuggestions);
            if (loadedSuggestions.length > 0) {
                setSelectedSuggestions([loadedSuggestions[0]]);
            } else {
                setSelectedSuggestions([]);
            }

            setIsLoading(false);
            setIsSuggestionsLoaded(true);

            return true;
        } catch (err) {
            console.error('Erreur lors du chargement des suggestions:', err);
            setErrorMessage((err as Error).message || 'Erreur inconnue');
            setIsLoading(false);
            return false;
        }
    }, [fetchSuggestions, modalActions, selectedSocietyId, setSuggestions, setSelectedSuggestions]);

    // Fonction pour gérer le passage à l'étape suivante depuis l'étape société
    const handleSocietyNext = React.useCallback(async () => {
        // Charger les suggestions
        const success = await loadSuggestions();

        if (success) {
            setCurrentStep('suggestion');
            forceRenderModal();
        }
    }, [forceRenderModal, loadSuggestions]);

    // Fonction pour gérer le passage à l'étape suivante depuis l'étape suggestions
    const handleSuggestionNext = React.useCallback(() => {
        if (state.selectedSuggestions.length === 0) {
            setErrorMessage('Veuillez sélectionner au moins un fournisseur');
            forceRenderModal();
            return;
        }
        setCurrentStep('message');
        setErrorMessage(null);
        forceRenderModal();
    }, [forceRenderModal, state.selectedSuggestions.length]);

    // Fonction pour gérer le retour à l'étape société
    const handleBackToSociety = React.useCallback(() => {
        setCurrentStep('society');
        setErrorMessage(null);
        forceRenderModal();
    }, [forceRenderModal]);

    // Fonction pour gérer le retour à l'étape suggestions
    const handleBackToSuggestion = React.useCallback(() => {
        setCurrentStep('suggestion');
        setErrorMessage(null);
        forceRenderModal();
    }, [forceRenderModal]);

    // Fonction pour gérer la création avec message
    const handleCreateWithMessage = React.useCallback(
        async (message: string) => {
            setIsCreatingReminders(true);
            modalActions.call(
                <ReminderStepperContent currentStep={currentStep}>
                    <LoadingModal message="Création des rappels en cours..." />
                </ReminderStepperContent>,
            );

            setCustomMessage(message);
            await createReminderPublishers(message);
        },
        [createReminderPublishers, setCustomMessage, modalActions, currentStep],
    );

    // Fonction pour gérer la création sans message
    const handleCreateWithoutMessage = React.useCallback(async () => {
        setIsCreatingReminders(true);
        modalActions.call(
            <ReminderStepperContent currentStep={currentStep}>
                <LoadingModal message="Création des rappels en cours..." />
            </ReminderStepperContent>,
        );

        await createReminderPublishers('');
    }, [createReminderPublishers, modalActions, currentStep]);

    /**
     * Ouvre et initialise la modal
     */
    const openModal = React.useCallback(() => {
        resetState();
        setSelectedSocietyId(12);
        setCurrentStep('society');
        setErrorMessage(null);
        setIsLoading(false);
        setIsCreatingReminders(false);
        setIsSuggestionsLoaded(false);
        renderCountRef.current = 0;

        setIsModalActive(true);
        forceRenderModal();
    }, [forceRenderModal, resetState]);

    // Effet pour afficher le contenu de la modal
    React.useEffect(() => {
        if (!isModalActive) return;

        // Limiter les rendus multiples
        renderCountRef.current += 1;
        if (renderCountRef.current > 10) {
            console.error('Trop de rendus détectés, arrêt pour éviter une boucle infinie');
            return;
        }

        // Si une erreur est définie, afficher l'erreur
        if (errorMessage) {
            modalActions.call(
                <ReminderStepperContent currentStep={currentStep}>
                    <EmptyBox text={errorMessage} />
                </ReminderStepperContent>,
            );
            return;
        }

        // Si chargement en cours (suggestions ou création), ne pas mettre à jour le contenu
        if (isLoading || isCreatingReminders) return;

        // Sinon, afficher le contenu correspondant à l'étape courante
        let content = null;

        if (currentStep === 'society') {
            content = (
                <SocietySelectionStep
                    selectedSociety={selectedSocietyId}
                    isProcessing={state.isProcessing}
                    onSocietyChange={handleSocietyChange}
                    onNext={handleSocietyNext}
                    onCancel={handleCloseModal}
                />
            );
        } else if (currentStep === 'suggestion' && isSuggestionsLoaded) {
            content = (
                <SuggestionSelectionStep
                    suggestions={suggestions}
                    isMultiSocietyMode={state.isMultiSocietyMode}
                    isProcessing={state.isProcessing}
                    onSuggestionChange={setSelectedSuggestions}
                    onBack={handleBackToSociety}
                    onNext={handleSuggestionNext}
                />
            );
        } else if (currentStep === 'message') {
            content = (
                <CustomMessageStep
                    customMessage={state.customMessage}
                    isProcessing={state.isProcessing}
                    suggestionCount={state.selectedSuggestions.length}
                    onSave={handleCreateWithMessage}
                    onCancel={handleCreateWithoutMessage}
                    onPrev={handleBackToSuggestion}
                />
            );
        }

        if (content) {
            modalActions.call(
                <ReminderStepperContent currentStep={currentStep}>
                    {content}
                </ReminderStepperContent>,
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        currentStep,
        errorMessage,
        isLoading,
        isCreatingReminders,
        isModalActive,
        isSuggestionsLoaded,
        renderTrigger,
        selectedSocietyId,
    ]);

    // Expose la méthode openModal via la référence
    React.useImperativeHandle(
        ref,
        () => ({
            openModal,
        }),
        [openModal],
    );

    return null;
});

ReminderPublisherModal.displayName = 'ReminderPublisherModal';
